<template>
  <div class="folder-select" :class="{ 'is-open': isOpen }" @keydown="handleKeydown">
    <input
      ref="input"
      type="text"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="String(isOpen)"
      :aria-controls="listId"
      :aria-activedescendant="activeOptionId"
      :value="isOpen ? query : selectedLabel"
      :placeholder="placeholder"
      :title="selectedLabel"
      autocomplete="off"
      @focus="openDropdown"
      @click="openDropdown"
      @input="handleInput"
      @blur="handleBlur"
    />
    <span class="folder-select-chevron" aria-hidden="true">
      <svg viewBox="0 0 16 16" width="16" height="16" focusable="false">
        <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </span>
    <div v-if="isOpen" :id="listId" class="folder-select-options" role="listbox">
      <button
        v-for="(option, index) in filteredOptions"
        :id="optionId(index)"
        :key="String(option.id)"
        type="button"
        class="folder-select-option"
        :class="{ active: index === activeIndex, selected: isSelected(option) }"
        :style="{ paddingLeft: `${10 + Math.max(0, Number(option._depth || 0) - 1) * 14}px` }"
        role="option"
        :aria-selected="String(isSelected(option))"
        @mouseenter="activeIndex = index"
        @mousedown.prevent
        @click="selectOption(option)"
      >
        <span>{{ option.name }}</span>
        <span v-if="isSelected(option)" class="folder-select-check" aria-hidden="true">✓</span>
      </button>
      <div v-if="!filteredOptions.length" class="folder-select-empty">没有匹配的目录</div>
    </div>
  </div>
</template>

<script>
let folderSelectInstance = 0;

export default {
  name: 'FolderSelect',
  props: {
    value: { type: [Number, String], default: 0 },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '选择目录' }
  },
  data() {
    folderSelectInstance += 1;
    return {
      instanceId: folderSelectInstance,
      isOpen: false,
      query: '',
      activeIndex: 0,
      blurTimer: null
    };
  },
  computed: {
    listId() { return `folder-select-list-${this.instanceId}`; },
    allOptions() { return [{ id: 0, name: '根目录', _depth: 0 }, ...this.options]; },
    selectedOption() { return this.allOptions.find(option => String(option.id) === String(this.value)) || null; },
    selectedLabel() { return this.selectedOption ? this.selectedOption.name : ''; },
    filteredOptions() {
      const query = this.query.trim().toLocaleLowerCase();
      if (!query) return this.allOptions;
      return this.allOptions.filter(option => `${option.name || ''} ${option.path || ''}`.toLocaleLowerCase().includes(query));
    },
    activeOptionId() { return this.isOpen && this.filteredOptions[this.activeIndex] ? this.optionId(this.activeIndex) : null; }
  },
  watch: {
    filteredOptions() { this.activeIndex = this.filteredOptions.length ? 0 : -1; }
  },
  beforeDestroy() {
    this.closeDropdown();
    if (this.blurTimer) clearTimeout(this.blurTimer);
  },
  methods: {
    optionId(index) { return `folder-select-option-${this.instanceId}-${index}`; },
    isSelected(option) { return String(option.id) === String(this.value); },
    openDropdown() {
      if (this.blurTimer) clearTimeout(this.blurTimer);
      if (this.isOpen) return;
      this.isOpen = true;
      this.query = '';
      const selectedIndex = this.filteredOptions.findIndex(option => this.isSelected(option));
      this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
      document.addEventListener('pointerdown', this.handleOutsidePointerDown);
    },
    closeDropdown() {
      this.isOpen = false;
      this.query = '';
      document.removeEventListener('pointerdown', this.handleOutsidePointerDown);
    },
    handleOutsidePointerDown(event) {
      if (!this.$el.contains(event.target)) this.closeDropdown();
    },
    handleInput(event) {
      this.query = event.target.value;
      this.isOpen = true;
      document.addEventListener('pointerdown', this.handleOutsidePointerDown);
    },
    handleBlur() {
      this.blurTimer = setTimeout(() => this.closeDropdown(), 0);
    },
    handleKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.isOpen) this.openDropdown();
        else if (this.filteredOptions.length) this.activeIndex = (this.activeIndex + 1) % this.filteredOptions.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.isOpen) this.openDropdown();
        else if (this.filteredOptions.length) this.activeIndex = (this.activeIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
      } else if (event.key === 'Enter' && this.isOpen && this.filteredOptions[this.activeIndex]) {
        event.preventDefault();
        this.selectOption(this.filteredOptions[this.activeIndex]);
      } else if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.closeDropdown();
      }
    },
    selectOption(option) {
      const id = Number(option.id);
      this.$emit('input', Number.isNaN(id) ? option.id : id);
      this.$emit('change', Number.isNaN(id) ? option.id : id);
      this.closeDropdown();
      this.$nextTick(() => this.$refs.input && this.$refs.input.blur());
    }
  }
};
</script>

<style scoped>
.folder-select {
  position: relative;
  flex: 1 1 120px;
  min-width: 120px;
  max-width: 170px;
  height: 34px;
  color: #718198;
  font-size: 12px;
}
.folder-select input {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 0 30px 0 10px;
  border: 1px solid #e1e8f2;
  border-radius: 9px;
  outline: 0;
  background: #fff;
  color: #718198;
  font: inherit;
}
.folder-select input:focus { border-color: #9fc5ff; box-shadow: 0 0 0 2px rgba(22,119,255,.1); }
.folder-select-chevron { position: absolute; top: 8px; right: 9px; display: flex; align-items: center; pointer-events: none; color: #8b99aa; }
.folder-select-chevron svg { display: block; }
.folder-select-options {
  position: absolute;
  z-index: 40;
  top: calc(100% + 5px);
  right: 0;
  left: 0;
  max-height: 280px;
  overflow: auto;
  padding: 5px;
  border: 1px solid #e1e8f2;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(45,80,130,.16);
}
.folder-select-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 34px;
  padding-top: 6px;
  padding-right: 10px;
  padding-bottom: 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #51627a;
  text-align: left;
  font: inherit;
  cursor: pointer;
}
.folder-select-option:hover,
.folder-select-option.active { background: #eaf3ff; color: #1677ff; }
.folder-select-option.selected { font-weight: 600; }
.folder-select-check { padding-left: 8px; color: #1677ff; }
.folder-select-empty { padding: 12px 10px; color: #8997ab; text-align: center; }
[data-theme="dark"] .folder-select input { border-color: #2a3a52; background: #141e2e; color: #dbeafe; }
[data-theme="dark"] .folder-select-options { border-color: #30445f; background: #1b2940; box-shadow: 0 12px 28px rgba(0,0,0,.35); }
[data-theme="dark"] .folder-select-option { color: #dbeafe; }
[data-theme="dark"] .folder-select-option:hover,
[data-theme="dark"] .folder-select-option.active { background: #223b5d; color: #8fc0ff; }
[data-theme="dark"] .folder-select-empty { color: #94a3b8; }
</style>
